(() => {
  const COUNTRY_KEY = 'fineyorker-country';
  const initializedHeaders = new WeakSet();
  const initializedProducts = new WeakSet();

  function readCountry() {
    try {
      return window.localStorage.getItem(COUNTRY_KEY) || 'US';
    } catch (error) {
      return 'US';
    }
  }

  function storeCountry(country) {
    try {
      window.localStorage.setItem(COUNTRY_KEY, country);
    } catch (error) {
      // Local storage is optional; the current selection still works.
    }
    document.dispatchEvent(new CustomEvent('fy:country', { detail: { country } }));
  }

  function initHeader(header) {
    if (initializedHeaders.has(header)) return;
    initializedHeaders.add(header);

    const toggle = header.querySelector('[data-fy-menu-toggle]');
    const nav = header.querySelector('[data-fy-nav]');
    const openLabel = toggle?.querySelector('[data-open-label]');
    const closeLabel = toggle?.querySelector('[data-close-label]');

    toggle?.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!open));
      nav?.classList.toggle('is-open', !open);
      if (openLabel) openLabel.hidden = !open;
      if (closeLabel) closeLabel.hidden = open;
    });

    header.querySelectorAll('[data-fy-country-select]').forEach((select) => {
      select.value = readCountry();
      select.addEventListener('change', () => storeCountry(select.value));
    });

    document.addEventListener('fy:country', (event) => {
      header.querySelectorAll('[data-fy-country-select]').forEach((select) => {
        if (select.value !== event.detail.country) select.value = event.detail.country;
      });
    });
  }

  function initProduct(root) {
    if (initializedProducts.has(root)) return;
    initializedProducts.add(root);

    const colorButtons = [...root.querySelectorAll('[data-fy-product-color]')];
    const queryColor = new URLSearchParams(window.location.search).get('color');
    const validQueryColor = colorButtons.some((button) => button.dataset.fyProductColor === queryColor) ? queryColor : null;
    let selectedColor = validQueryColor || root.dataset.defaultColor || 'teal';
    const mainImage = root.querySelector('.fy-product-main-image__asset');
    const mainVideo = root.querySelector('.fy-product-main-image__video');
    const galleryThumbs = root.querySelector('[data-fy-gallery-thumbs]');
    const mediaSources = [...root.querySelectorAll('[data-fy-color-media-source]')];
    const amazonButton = root.querySelector('[data-fy-amazon-button]');
    const countrySelect = root.querySelector('[data-fy-product-country]');
    let galleryButtons = [];

    function setMainImage(url, alt) {
      if (!mainImage || !url) return;
      if (mainVideo) {
        mainVideo.pause();
        mainVideo.hidden = true;
        mainVideo.removeAttribute('src');
      }
      mainImage.hidden = false;
      mainImage.src = url;
      mainImage.srcset = '';
      mainImage.alt = alt || '';
    }

    function setMainVideo(url, poster, alt) {
      if (!mainVideo || !url) return;
      mainImage.hidden = true;
      mainVideo.hidden = false;
      mainVideo.src = url;
      if (poster) mainVideo.poster = poster;
      mainVideo.setAttribute('aria-label', alt || 'Product video');
      mainVideo.load();
    }

    function bindGalleryButtons() {
      galleryButtons = [...(galleryThumbs?.querySelectorAll('[data-fy-gallery-type]') || [])];
      galleryButtons.forEach((button) => {
        button.addEventListener('click', () => {
          galleryButtons.forEach((item) => item.classList.toggle('is-active', item === button));
          const type = button.dataset.fyGalleryType || 'image';
          if (type === 'video') {
            setMainVideo(button.dataset.fyGalleryVideo, button.dataset.fyGalleryPoster, button.dataset.imageAlt);
          } else {
            setMainImage(button.dataset.fyGalleryImage, button.dataset.imageAlt);
          }
        });
      });
    }

    function renderColorGallery(color) {
      if (!galleryThumbs) return;
      const source = mediaSources.find((item) => item.dataset.fyColorMediaSource === color);
      if (!source) return;
      galleryThumbs.replaceChildren(source.content.cloneNode(true));
      bindGalleryButtons();
      galleryButtons[0]?.click();
    }

    function updateColorUrl(color) {
      const url = new URL(window.location.href);
      url.searchParams.set('color', color);
      window.history.replaceState({}, '', url);
    }

    function updateAmazonLink() {
      if (!amazonButton) return;
      const country = (countrySelect?.value || readCountry()).toLowerCase();
      const configured = root.getAttribute(`data-amazon-${country}-${selectedColor}`);
      const destination = configured || '#';
      const disabled = destination === '#';
      amazonButton.href = destination;
      amazonButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      amazonButton.classList.toggle('is-disabled', disabled);
      amazonButton.textContent = disabled
        ? amazonButton.dataset.fyComingSoonLabel || 'Coming soon'
        : amazonButton.dataset.fyAmazonLabel || 'Shop on Amazon';
    }

    colorButtons.forEach((button) => {
      button.addEventListener('click', () => {
        selectedColor = button.dataset.fyProductColor;
        colorButtons.forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-selected', active);
          item.setAttribute('aria-pressed', String(active));
        });
        updateColorUrl(selectedColor);
        renderColorGallery(selectedColor);
        updateAmazonLink();
      });
    });

    if (countrySelect) {
      countrySelect.value = readCountry();
      countrySelect.addEventListener('change', () => {
        storeCountry(countrySelect.value);
        updateAmazonLink();
      });
    }

    document.addEventListener('fy:country', (event) => {
      if (countrySelect && countrySelect.value !== event.detail.country) countrySelect.value = event.detail.country;
      updateAmazonLink();
    });

    const initialColorButton = colorButtons.find((button) => button.dataset.fyProductColor === selectedColor) || colorButtons[0];
    if (initialColorButton) {
      selectedColor = initialColorButton.dataset.fyProductColor;
      colorButtons.forEach((item) => {
        const active = item === initialColorButton;
        item.classList.toggle('is-selected', active);
        item.setAttribute('aria-pressed', String(active));
      });
    }
    renderColorGallery(selectedColor);
    updateAmazonLink();
    amazonButton?.addEventListener('click', (event) => {
      if (amazonButton.getAttribute('aria-disabled') === 'true') event.preventDefault();
    });
  }

  function initialize(scope = document) {
    scope.querySelectorAll('[data-fy-header]').forEach(initHeader);
    scope.querySelectorAll('[data-fy-product]').forEach(initProduct);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
